/* Y2K shared runtime: toasts, sparkle trail, sound engine, chrome behaviors. */
const SETTINGS = () => window.Y2K_SETTINGS || {};
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- sound engine ----------
   Storage note: touching `localStorage` THROWS (SecurityError) — it does not return null — when
   the browser blocks site data (Safari "Block all cookies", Chrome's third-party-context block,
   Firefox strict mode). Both accessors are guarded because `sound.enabled` is read by every
   blip()/buzz() and written by the toolbar toggle, and all of those run inside wireChrome's
   click branches: an unguarded throw in the cart-pill branch would abort AFTER
   e.preventDefault(), leaving the drawer unopened and the anchor dead. */
let soundMemory = null; // session-only fallback used only when the storage write is refused
const sound = {
  _ctx: null,
  get enabled() {
    try {
      const stored = localStorage.getItem('y2k-sound');
      if (stored !== null) return stored === '1';
    } catch (_) {
      // Storage blocked: honour a toggle made this session, else the merchant default.
      if (soundMemory !== null) return soundMemory;
    }
    return SETTINGS().sounds !== false;
  },
  set enabled(v) {
    try {
      localStorage.setItem('y2k-sound', v ? '1' : '0');
    } catch (_) {
      soundMemory = v; // preference holds for this page load only
    }
    document.querySelectorAll('[data-y2k-sound-toggle]').forEach((el) => {
      el.setAttribute('aria-pressed', String(v));
      el.textContent = v ? 'sounds: on' : 'sounds: off';
    });
  },
  _ensure() {
    if (!this._ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) this._ctx = new AC();
    }
    if (this._ctx && this._ctx.state === 'suspended') this._ctx.resume();
    return this._ctx;
  },
  /* Drop, don't queue, any note requested before the shopper's first real gesture (the
     messenger's boot lines blip on page-load timers): a pre-activation AudioContext is born
     suspended and logs autoplay warnings, and oscillators scheduled on its frozen clock all
     pile up at t=0 — they'd burst out together the moment the first click resumes it.
     `navigator.userActivation` is unsupported only in old engines, where the short-circuit
     keeps the old behaviour. */
  _gestureReady() {
    return !(navigator.userActivation && !navigator.userActivation.hasBeenActive);
  },
  blip(freq = 880, dur = 0.08, type = 'square', gain = 0.03) {
    if (!this.enabled || !this._gestureReady()) return;
    const ctx = this._ensure();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur);
  },
  buzz() {
    if (!this.enabled || !this._gestureReady()) return;
    const ctx = this._ensure();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.value = 95;
    g.gain.setValueAtTime(0.05, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.38);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.38);
  },
};

/* ---------- favourites store ----------
   Device-local by design: there is no storefront-writable per-customer store in theme code,
   so "saved to my account, visible on my phone" is out of scope without an app.

   NOTE the sound accessors above are deliberately NOT refactored onto safeStorage: they must
   tell "storage blocked" apart from "key absent" (only the former falls back to soundMemory),
   and a helper that returns null for both would break that. */
const safeStorage = {
  get(key) { try { return localStorage.getItem(key); } catch (_) { return null; } },
  set(key, value) { try { localStorage.setItem(key, value); return true; } catch (_) { return false; } },
};

const FAVS_KEY = 'y2k-favs';
const FAVS_MAX = 60; // bounded storage; ~150 bytes per snapshot
let favsMemory = null; // session-only fallback when the storage write is refused

const favs = {
  _read() {
    // A fresh copy every call, same as the JSON.parse path below: callers (list rendering,
    // toggle's own dedup filter) are free to sort/splice the result without corrupting the
    // session cache or silently bypassing the FAVS_MAX cap on the next _write().
    if (favsMemory !== null) return favsMemory.slice();
    const raw = safeStorage.get(FAVS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Stored data is attacker-influenced only via the user's own devtools, but a corrupt or
      // hand-edited value must never throw downstream — drop anything without a handle.
      return parsed.filter((s) => s && typeof s === 'object' && typeof s.handle === 'string');
    } catch (_) {
      return [];
    }
  },
  _write(list) {
    const capped = list.slice(0, FAVS_MAX);
    // JSON.stringify can throw (circular refs, BigInt) same as the storage call it feeds —
    // guard it too, so a bad snapshot degrades to the session fallback instead of throwing
    // out of toggle() mid-click-handler.
    let serialized;
    try {
      serialized = JSON.stringify(capped);
    } catch (_) {
      serialized = null;
    }
    if (serialized === null || !safeStorage.set(FAVS_KEY, serialized)) favsMemory = capped;
    else favsMemory = null;
    return capped;
  },
  list() { return this._read(); },
  count() { return this._read().length; },
  has(handle) { return this._read().some((s) => s.handle === handle); },
  remove(handle) {
    this._write(this._read().filter((s) => s.handle !== handle));
    this._sync();
    /* Also on removal, unlike the event: someone who un-stars everything must fall OUT of the
       segment, or they keep receiving mail about pieces they no longer want. */
    syncFavsToContact();
  },
  /* Returns the NEW state: true = now favourited. */
  toggle(snapshot) {
    const list = this._read().filter((s) => s.handle !== snapshot.handle);
    const added = list.length === this._read().length;
    if (added) list.unshift(snapshot);
    this._write(list);
    this._sync();
    /* Only on the way IN. Un-starring is not an event worth mailing about, and firing on both
       would let someone toggling a star produce unlimited events. */
    if (added) {
      // Keeps the segment membership current for a signed-in shopper; no-op when logged out.
      syncFavsToContact();
      trackEvent('favouriteAdded', {
        handle: snapshot.handle,
        title: snapshot.title || snapshot.handle,
        url: snapshot.url || `/products/${snapshot.handle}`,
        image: snapshot.image || '',
        price: snapshot.price || '',
        favouritesCount: list.length,
      });
    }
    return added;
  },
  /* Push state OUT to the DOM. Called after every mutation, and by rehydrate() whenever new
     cards appear — y2k-related.liquid:40 replaceWith()s its whole grid after a fetch, so
     binding state at render time would silently lose it.
     Idempotent by construction, same as fixModalPrices/fixCartPrices: wireChrome's #MainContent
     observer watches { childList: true, subtree: true } and calls rehydrate() -> _sync() on any
     addedNodes. A textContent assignment is itself a childList mutation (replace-all swaps the
     text node even when the string is unchanged), so writing unconditionally would re-queue the
     observer callback forever — every _sync() begets another _sync(). Only write when the value
     actually differs, so the second pass has nothing left to change and the observer settles. */
  _sync() {
    const handles = new Set(this._read().map((s) => s.handle));
    document.querySelectorAll('[data-y2k-fav]').forEach((btn) => {
      const on = handles.has(btn.dataset.favHandle);
      const glyph = on ? '★' : '☆'; // ★ / ☆
      if (btn.getAttribute('aria-pressed') !== String(on)) btn.setAttribute('aria-pressed', String(on));
      if (btn.textContent !== glyph) btn.textContent = glyph;
    });
    const n = this.count();
    const nStr = String(n);
    document.querySelectorAll('[data-y2k-favs-count]').forEach((el) => {
      if (el.textContent !== nStr) el.textContent = nStr;
      if (el.hidden !== (n === 0)) el.toggleAttribute('hidden', n === 0);
    });
  },
  rehydrate() { this._sync(); },
};

/* ---------- toast manager (queue of 1; MSN mini-window) ----------
   The live region is created ONCE (wireChrome) and then stays in the DOM, empty, forever:
   screen readers announce reliably into a region that already existed when the text lands,
   and frequently miss one that is inserted together with its text. toast() therefore only
   swaps the region's contents — the region element itself is never removed. The visible
   .y2k-toast box stays a child so all its styling (position: fixed, animation) is unchanged. */
let toastRegion = null;
let toastTimer = 0;
function ensureToastRegion() {
  if (toastRegion?.isConnected) return toastRegion;
  toastRegion = document.createElement('div');
  toastRegion.className = 'y2k-toast-region';
  toastRegion.setAttribute('role', 'status');
  toastRegion.setAttribute('aria-live', 'polite');
  document.body.append(toastRegion);
  return toastRegion;
}
function toast(message, title = 'brat explorer') {
  const region = ensureToastRegion();
  clearTimeout(toastTimer);
  const box = document.createElement('div');
  box.className = 'y2k-toast';
  const bar = document.createElement('div');
  bar.className = 'y2k-toast__bar';
  bar.textContent = title;
  const body = document.createElement('div');
  body.className = 'y2k-toast__body';
  body.textContent = message;
  box.append(bar, body);
  region.replaceChildren(box); // drops any previous toast; the live region survives
  toastTimer = setTimeout(() => region.replaceChildren(), 3600);
}

/* ---------- sparkle cursor trail ---------- */
const SPARK_COLORS = ['#ff2d95', '#ffd1ec', '#9fd8ff', '#ffffff'];
let lastSpark = 0;
function sparkMove(e) {
  const now = performance.now();
  if (now - lastSpark < 55) return;
  lastSpark = now;
  const s = document.createElement('span');
  s.className = 'bz-spark';
  s.textContent = Math.random() > 0.5 ? '✦' : '⋆';
  s.style.left = e.clientX + (Math.random() * 18 - 9) + 'px';
  s.style.top = e.clientY + (Math.random() * 18 - 9) + 'px';
  s.style.color = SPARK_COLORS[(Math.random() * SPARK_COLORS.length) | 0];
  s.style.fontSize = 10 + Math.random() * 12 + 'px';
  document.body.append(s);
  setTimeout(() => s.remove(), 900);
}
function initSparkle() {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (SETTINGS().sparkle === false || !fine.matches || reducedMotion.matches) return;
  document.addEventListener('pointermove', sparkMove, { passive: true });
}

/* ---------- cart count + drawer ---------- */
async function refreshCartCount() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    document.querySelectorAll('[data-y2k-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
    });
    window.y2k._cartVariants = new Set(cart.items.map((i) => i.variant_id));
  } catch (_) { /* leave stale count */ }
}
function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') drawer.open();
  else window.location.href = '/cart';
}

/* ---------- mega menu ----------
   The panel is opened by CSS (:hover / :focus-within) so it works with JS still loading and
   for keyboard users for free. JS adds only what CSS cannot express: a touch toggle, Escape,
   and click-outside. See the [data-closed] note in y2k-chrome-header.liquid's stylesheet. */
function closeMegaPanels() {
  document.querySelectorAll('.y2k-navitem[data-open]').forEach((item) => {
    item.removeAttribute('data-open');
    item.querySelector('[data-y2k-mega-toggle]')?.setAttribute('aria-expanded', 'false');
  });
}
function closeFavsPanel() {
  document.querySelectorAll('[data-y2k-favs-toggle][aria-expanded="true"]').forEach((btn) => {
    btn.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
  });
}
/* Rendered from the stored snapshots rather than fetched per handle: a dropdown has to open
   instantly, and N product requests on every open would be pointless when a row is a thumbnail,
   a title and a price. The PDP behind each row stays the source of truth.
   Built with createElement/textContent, never innerHTML — titles are merchant data and must not
   be parsed as markup. The panel lives in the header, outside #MainContent, so rebuilding it
   does not trip the favourites MutationObserver. */
function renderFavsPanel() {
  const body = document.querySelector('[data-y2k-favs-body]');
  if (!body) return;
  const items = favs.list();
  body.textContent = '';

  if (!items.length) {
    const empty = document.createElement('p');
    empty.className = 'y2k-favs__empty';
    empty.textContent = 'nothing starred yet — tap the ☆ on anything u love ♡';
    body.append(empty);
    return;
  }

  const rowsByHandle = new Map();
  items.forEach((item) => {
    const row = document.createElement('div');
    row.className = 'y2k-favrow';
    row.dataset.favRow = item.handle;

    const link = document.createElement('a');
    link.className = 'y2k-favrow__link y2k-focusable';
    link.href = item.url || `/products/${item.handle}`;

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'y2k-favrow__img';
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      link.append(img);
    }

    const text = document.createElement('span');
    text.className = 'y2k-favrow__text';
    const name = document.createElement('span');
    name.className = 'y2k-favrow__name';
    name.textContent = item.title || item.handle;
    const price = document.createElement('span');
    price.className = 'y2k-favrow__price';
    price.textContent = item.price || '';
    text.append(name, price);
    link.append(text);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'y2k-favrow__remove y2k-focusable';
    remove.dataset.y2kFavRemove = item.handle;
    remove.setAttribute('aria-label', `remove ${item.title || item.handle} from favourites`);
    remove.textContent = '✕';

    row.append(link, remove);
    body.append(row);
    rowsByHandle.set(item.handle, row);
  });

  /* Availability pass. Every product here is a one-of-one, so a favourite starred last week may
     already be gone — a stale "available" row is a real disappointment, not a cosmetic bug.
     Fires only on open, only for what is listed, and capped. Failures are SILENT: a network
     error must leave the row exactly as rendered rather than falsely marking it sold. */
  items.slice(0, 12).forEach((item) => {
    const row = rowsByHandle.get(item.handle);
    if (!row) return;
    Promise.resolve()
      .then(() => fetch(`/products/${encodeURIComponent(item.handle)}.js`))
      .then((r) => (r && r.ok ? r.json() : null))
      .then((data) => {
        if (!data || data.available !== false) return;
        row.dataset.soldOut = 'true';
        const price = row.querySelector('.y2k-favrow__price');
        if (price) price.textContent = 'gone ✧';
        /* KNOWN LIMITATION, stated so nobody mistakes this for reliable sold-out notification:
           this pass only runs when the shopper OPENS their favourites panel, so the event reaches
           people who came back — not the ones who starred something and never returned, who are
           exactly the ones an automation would want. Real coverage needs favourites stored
           server-side and a scheduled job. This is the honest client-side half. */
        if (!alreadyReportedGone(item.handle)) {
          trackEvent('favouriteSoldOut', {
            handle: item.handle,
            title: item.title || item.handle,
            url: item.url || `/products/${item.handle}`,
          });
        }
      })
      // Silent by design — a network failure must leave the row as rendered. Holding the row
      // reference rather than re-querying by handle keeps this catch to genuine network errors:
      // an attribute selector would need CSS.escape, whose absence would throw in here unseen.
      .catch(() => {});
  });
}

/* ---------- declarative chrome behaviors ---------- */
function wireChrome() {
  ensureToastRegion();

  // Escape latch: cleared once the pointer or focus genuinely leaves the item, so the panel
  // becomes hoverable/focusable again on the next approach.
  document.addEventListener('focusout', (e) => {
    const item = e.target.closest?.('.y2k-navitem--panel');
    // relatedTarget is where focus went; null (e.g. focus left the document) also counts as out.
    if (item && !item.contains(e.relatedTarget)) item.removeAttribute('data-closed');
  });
  document.addEventListener('mouseleave', (e) => {
    if (e.target.classList?.contains('y2k-navitem--panel')) e.target.removeAttribute('data-closed');
  }, true); // capture: mouseleave does not bubble

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const favsWrap = e.target.closest?.('.y2k-favs');
    if (favsWrap) {
      closeFavsPanel();
      favsWrap.querySelector('[data-y2k-favs-toggle]')?.focus();
      return;
    }
    const item = e.target.closest?.('.y2k-navitem--panel');
    if (!item) return;
    item.setAttribute('data-closed', '');
    closeMegaPanels();
    item.querySelector('[data-y2k-mega-toggle]')?.focus();
  });

  document.addEventListener('click', (e) => {
    // Click-outside closes. Runs before every other branch so it still fires when a click is
    // handled by one of them (the cart pill, for instance, returns early).
    if (!e.target.closest('.y2k-navitem--panel')) closeMegaPanels();
    if (!e.target.closest('.y2k-favs')) closeFavsPanel();
    const megaToggle = e.target.closest('[data-y2k-mega-toggle]');
    if (megaToggle) {
      const item = megaToggle.closest('.y2k-navitem');
      const panel = item.querySelector('.y2k-mega');
      /* How "is it already open?" is decided has to differ by input type, and getting this wrong
         is what made the menu look broken on phones.

         POINTER: :hover has already opened the panel before the click lands, so [data-open]
         alone would report "closed" and the first click would be swallowed. Read real visibility.

         TOUCH: a tap FOCUSES the button before it clicks it, and :focus-within opens the panel —
         so by the time this handler runs the panel is visibly open, computed visibility reports
         "already open", and the handler closes the thing the tap was trying to open. The first
         tap appeared to do nothing and only the second worked. On touch the toggle's own
         aria-expanded is the honest record of intent, because focus does not change it. */
      const canHover = window.matchMedia('(hover: hover)').matches;
      const isOpen = canHover
        ? !!panel && getComputedStyle(panel).display !== 'none'
        : megaToggle.getAttribute('aria-expanded') === 'true';
      closeMegaPanels();
      if (isOpen) {
        // Clearing [data-open] hides nothing while :hover or :focus-within still match — the
        // latch is what actually closes it. Cleared again on focusout/mouseleave below.
        item.setAttribute('data-closed', '');
        megaToggle.setAttribute('aria-expanded', 'false');
      } else {
        item.removeAttribute('data-closed');
        item.setAttribute('data-open', '');
        megaToggle.setAttribute('aria-expanded', 'true');
      }
      sound.blip(740, 0.05);
      return;
    }
    const favBtn = e.target.closest('[data-y2k-fav]');
    if (favBtn) {
      // The star sits OUTSIDE the card anchor (interactive content cannot nest in <a>), so
      // there is no navigation to cancel — but stop the event reaching any wrapping handler.
      e.preventDefault();
      const added = favs.toggle({
        handle: favBtn.dataset.favHandle,
        title: favBtn.dataset.favTitle || '',
        url: favBtn.dataset.favUrl || '',
        image: favBtn.dataset.favImage || '',
        price: favBtn.dataset.favPrice || '',
      });
      sound.blip(added ? 1180 : 520, 0.07, 'sine');
      return;
    }
    const favRemove = e.target.closest('[data-y2k-fav-remove]');
    if (favRemove) {
      e.preventDefault();
      sound.blip(520, 0.07, 'sine');
      favs.remove(favRemove.dataset.y2kFavRemove);
      renderFavsPanel();
      return;
    }
    const favsToggle = e.target.closest('[data-y2k-favs-toggle]');
    if (favsToggle) {
      const panel = document.getElementById(favsToggle.getAttribute('aria-controls'));
      const open = panel.hidden;
      panel.hidden = !open;
      favsToggle.setAttribute('aria-expanded', String(open));
      if (open) { sound.blip(880, 0.06, 'sine'); renderFavsPanel(); }
      return;
    }
    const back = e.target.closest('[data-y2k-back]');
    if (back) {
      sound.blip(660);
      if (history.length > 1) history.back(); else window.location.href = '/';
      return;
    }
    const t = e.target.closest('[data-y2k-toast]');
    if (t) {
      if (t.dataset.toastOnce === 'done') { sound.blip(220, 0.09); return; }
      if (t.hasAttribute('data-toast-once')) t.dataset.toastOnce = 'done';
      sound.blip(1100, 0.07, 'sine');
      toast(t.dataset.toastMessage, t.dataset.toastTitle || 'brat explorer');
      return;
    }
    const cartOpen = e.target.closest('[data-y2k-cart-open]');
    if (cartOpen) { e.preventDefault(); sound.blip(990, 0.08, 'sine', 0.05); openCartDrawer(); return; }
    const beep = e.target.closest('[data-y2k-beep]');
    if (beep) { sound.blip(880); return; }
    const soundToggle = e.target.closest('[data-y2k-sound-toggle]');
    if (soundToggle) { sound.enabled = !sound.enabled; if (sound.enabled) sound.blip(990, 0.08, 'sine'); return; }
  });
  // Real dispatched event type (assets/events.js): ThemeEvents.cartUpdate = 'cart:update'.
  // Both CartAddEvent and CartUpdateEvent dispatch this same type string (see investigation
  // notes in the task report); there is no separate 'cart:add' type, and the class names
  // 'CartAddEvent'/'CartUpdateEvent' are never used as event types, so a single listener
  // on 'cart:update' covers both cart additions and updates.
  document.addEventListener('cart:update', refreshCartCount);

  const auto = document.querySelector('[data-y2k-autotoast]');
  if (auto) toast(auto.dataset.toastMessage, auto.dataset.toastTitle || 'brat explorer');

  /* The authored facet form (snippets/_y2k-facets.liquid) is a plain GET form; empty price
     inputs would submit `filter.v.price.gte=` — Shopify ignores the empty param, this just
     keeps the URLs clean (the stock facets.js strips the same params before pushState).
     Disabled-on-submit fields are excluded from the GET serialization; checkboxes are
     naturally excluded when unchecked. Capture phase so it runs before navigation starts. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.classList || !form.classList.contains('y2k-facets')) return;
    form.querySelectorAll('input').forEach((input) => {
      if (input.type !== 'checkbox' && input.value === '') input.disabled = true;
    });
  }, true);
  /* The disabling above mutates live DOM right before a GET navigation, and Back restores
     that DOM verbatim from the bfcache in every current browser — without this, the price
     inputs would come back permanently dead after any filter submit (review finding). The
     server never renders disabled fields in these forms, so blanket re-enabling is safe. */
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.y2k-facets input[disabled]').forEach((input) => { input.disabled = false; });
  });

  favs.rehydrate();
  /* The related-products grid replaces itself after a fetch; re-apply star state when it does.
     A MutationObserver rather than hooking that section keeps this working for any future
     surface that swaps cards in. */
  const cardHost = document.getElementById('MainContent');
  if (cardHost) {
    new MutationObserver((records) => {
      if (records.some((r) => r.addedNodes.length)) favs.rehydrate();
    }).observe(cardHost, { childList: true, subtree: true });
  }
}

/* ---------- PDP price format: "$69.00" → "$69 aud" ----------
   Spec asks for "$69 aud"; the stock price block renders the shop's money format. The chain is
   blocks/price.liquid → snippets/price.liquid, which prints `{{ price | money }}` into
   <product-price …><div ref="priceContainer"><span class="price">$69.00</span></div>, so the
   rendered text is the only lever — the money format is a shop-level setting and price.liquid
   is stock code we are not forking.
   Deliberately narrow: ONLY exact whole-dollar output is rewritten — bare ("$69.00") or with
   the currency code the live PDP actually renders ("$69.00 AUD" → "$69 aud", live fix 7f94b19).
   A piece priced at $69.50 is left verbatim. Same `.product-details product-price`
   scope as the PDP price CSS in y2k.css, so related-product card prices are untouched.
   TASK 20 MUST VERIFY this against the rendered DOM: that the selector matches, and that the
   live money format really is the "$69.00" form this regex expects. */
const WHOLE_DOLLARS = /^\$([\d,]+)\.00(\s*AUD)?$/i;
function fixPdpPrice(root) {
  root.querySelectorAll('.product-details product-price :is(.price, .compare-at-price)').forEach((el) => {
    const text = el.textContent.trim();
    const m = WHOLE_DOLLARS.exec(text);
    if (!m) return;
    el.textContent = `$${m[1]} aud`; // textContent both ways: never re-parses markup
  });
}
function initPdpPrice() {
  const main = document.getElementById('MainContent');
  if (!main || !(main.dataset.template || '').startsWith('product')) return;
  fixPdpPrice(main);
  /* assets/events.js: ThemeEvents.variantUpdate = 'variant:update' — the only variant event
     carrying re-rendered markup. ProductPrice (assets/product-price.js) swaps in that markup
     from a listener on the closest .shopify-section, i.e. earlier in the same bubble path than
     this document-level one; the rAF is belt-and-braces for any async morph. Every piece is a
     single variant today, so this is a nicety, not the main path. */
  document.addEventListener('variant:update', () => requestAnimationFrame(() => fixPdpPrice(main)));
}

/* Search-modal card prices get the same brand format ("$75.00 AUD" → "$75 aud"). The modal's
   results morph in asynchronously (predictive-search.js fetches Section Rendering markup), so
   watch the subtree instead of hooking its internals. Idempotent by construction: a rewritten
   node no longer matches WHOLE_DOLLARS, so the observer settles after one extra pass. */
function fixModalPrices(modal) {
  modal.querySelectorAll('.price').forEach((el) => {
    const m = WHOLE_DOLLARS.exec(el.textContent.trim());
    if (m) el.textContent = `$${m[1]} aud`;
  });
}
function initModalPrices() {
  const modal = document.getElementById('search-modal');
  if (!modal || typeof MutationObserver === 'undefined') return;
  fixModalPrices(modal);
  new MutationObserver(() => fixModalPrices(modal)).observe(modal, { childList: true, subtree: true });
}

/* Cart page gets the brand price format too. Unlike the PDP spans these containers can hold
   child elements (compare-at price spans), so only leaf elements are rewritten — flattening
   children with textContent would destroy them. Quantity changes morph the section, hence the
   observer; idempotent for the same reason as the modal fixer. */
function fixCartPrices(main) {
  main.querySelectorAll('.cart-items__price, .cart__total-value, .cart__original-total-value, .cart__discount-value').forEach((container) => {
    // Line prices wrap the text in <text-component value="...">; totals are bare text. Only
    // rewrite an unambiguous leaf — anything else (compare-at spans etc.) keeps its markup.
    let leaf = null;
    if (container.children.length === 0) leaf = container;
    else if (container.children.length === 1
      && container.firstElementChild.tagName === 'TEXT-COMPONENT'
      && container.firstElementChild.children.length === 0) leaf = container.firstElementChild;
    if (!leaf) return;
    const m = WHOLE_DOLLARS.exec(leaf.textContent.trim());
    if (!m) return;
    leaf.textContent = `$${m[1]} aud`;
    // Keep the component's source-of-truth attribute in sync or it restores the old text.
    if (leaf.tagName === 'TEXT-COMPONENT') leaf.setAttribute('value', `$${m[1]} aud`);
  });
}
function initCartPrices() {
  const main = document.getElementById('MainContent');
  if (!main || (main.dataset.template || '') !== 'cart' || typeof MutationObserver === 'undefined') return;
  fixCartPrices(main);
  new MutationObserver(() => fixCartPrices(main)).observe(main, { childList: true, subtree: true });
}

/* ---------- PDP 1-of-1 guard (product templates only) ----------
   Every piece is a single unit of stock, so a second add of the same variant is always a
   mistake. The theme binds the add declaratively: blocks/buy-buttons.liquid puts
   `on:submit="/handleSubmit"` on <product-form-component>, and assets/component.js registers
   ONE document-level listener per event type with { capture: true }, resolving the handler at
   event time (`const callback = instance[method]` → `callback.call(instance, event)`).
   Two consequences, both verified against those files:
     1. Assigning an own `handleSubmit` on the instance shadows ProductFormComponent.prototype
        .handleSubmit and intercepts the add.
     2. A listener added on the form (or the component) could NOT intercept — capture runs
        document → target, so the theme's dispatcher always fires first.
   The instance survives variant changes: ProductFormComponent#onVariantUpdate morphs the
   button and rewrites refs.variantId.value in place, it never replaces the element. */
function installPdpGuard(main) {
  const form = main.querySelector('product-form-component');
  if (!form) return;
  // Idempotent: our override is an OWN property while the theme's lives on the prototype,
  // so an own `handleSubmit` means the guard is already installed.
  if (Object.prototype.hasOwnProperty.call(form, 'handleSubmit')) return;
  if (typeof form.handleSubmit !== 'function') return;
  const original = form.handleSubmit.bind(form);
  form.handleSubmit = function (event) {
    const variantInput = form.refs?.variantId || form.querySelector('input[name="id"]');
    const variantId = Number(variantInput?.value);
    // Fails open: if /cart.js never answered, _cartVariants is empty and the add proceeds.
    if (variantId && window.y2k._cartVariants.has(variantId)) {
      event.preventDefault();
      const button =
        form.refs?.addToCartButtonContainer?.refs?.addToCartButton ||
        form.querySelector('button[type="submit"][name="add"]') ||
        form.querySelector('button[type="submit"]');
      /* Undo the click feedback. The same button also carries `on:click="/handleClick"`
         (snippets/add-to-cart-button.liquid), and AddToCartComponent.handleClick runs
         animateAddToCart() + #animateFlyToCart() UNCONDITIONALLY (product-form.js:57-61) —
         it has no idea the add is about to be blocked. Without this, a blocked duplicate
         shows "Added ✓" sliding in and a thumbnail flying to the cart while the toast says
         the piece is already in there. Safe to undo synchronously: the submit event is the
         click's activation behaviour, so we are still inside the same task as handleClick and
         the browser has not painted the added state yet. */
      button?.classList.remove('atc-added'); // mirrors animateAddToCart(), product-form.js:101
      document.querySelectorAll('fly-to-cart').forEach((el) => {
        // #animateFlyToCart stamps `source` with the button it launched from (product-form.js:85),
        // so this only reaps the element our own blocked click just spawned.
        if (!el.source || el.source === button) el.remove();
      });
      sound.blip(300, 0.09);
      toast("she's literally one of one bb — already in ur cart ♡");
      // Retarget the label span (snippets/add-to-cart-button.liquid) so the theme's icon and
      // its "added" overlay markup survive; fall back to the button for other markup.
      const label = button?.querySelector('.add-to-cart-text__content') || button;
      if (label) label.textContent = 'in ur cart ♡';
      return;
    }
    return original(event);
  };
}
function initPdpGuard() {
  const main = document.getElementById('MainContent');
  if (!main || !(main.dataset.template || '').startsWith('product')) return;
  // y2k.js and product-form.js are independent modules with no guaranteed order. Wait for the
  // definition: customElements.define() upgrades already-parsed elements synchronously, so by
  // the time this resolves `form.handleSubmit` is on the prototype and safe to shadow.
  customElements.whenDefined('product-form-component').then(() => installPdpGuard(main));
}

/* ---------- <y2k-rail>: shared horizontal carousel ----------
   Registered here rather than in a section's inline <script type="module"> because two
   sections use it and each would otherwise re-register (and the second define() throws).
   Deliberately NOT built on the stock <slideshow-component>: its arrows inherit
   --color-foreground (white-on-pale under the Y2K skin) and carry stock locale labels. */
if (!customElements.get('y2k-rail')) {
  customElements.define('y2k-rail', class extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-rail-track]');
      if (!track) return;
      const btns = [...this.querySelectorAll('[data-rail-dir]')];

      // Snap to whole items: measure a real child rather than assuming a width, since the
      // item width is a CSS custom property the sections override per surface.
      const step = () => {
        const first = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 14;
        return first ? first.getBoundingClientRect().width + gap : 220;
      };

      const sync = () => {
        // 2px slack: fractional scroll offsets never land exactly on the maximum.
        const max = track.scrollWidth - track.clientWidth;
        const atStart = track.scrollLeft <= 2;
        const atEnd = track.scrollLeft >= max - 2;
        btns.forEach((b) => {
          b.disabled = b.dataset.railDir === 'prev' ? atStart : atEnd;
        });
        // A track that does not overflow needs no controls at all.
        this.querySelector('.y2k-rail-nav')?.toggleAttribute('hidden', max <= 2);
      };

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-rail-dir]');
        if (!btn || btn.disabled) return;
        sound.blip(740, 0.05);
        track.scrollBy({
          left: (btn.dataset.railDir === 'prev' ? -1 : 1) * step(),
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    }
  });
}

/* ---------- email capture -> Omnisend identification ----------
   Shopify's own forms create a consented customer but set no Omnisend cookie, so a shopper who
   just handed over their email stays anonymous to every automation. This links the browser to the
   contact at the moment they submit.

   Delegated and attribute-driven rather than bound to one selector: the bundle builder and the
   favourites panel both grow an email field later, and each opts in with `data-y2k-identify` on
   the form or any ancestor — no second copy of this logic.

   Fires on submit rather than on a `?customer_posted=true` return, because Liquid never exposes
   the submitted address back to the page. checkValidity() gates it so a typo is never pushed. */
function identifyContact(email, extra) {
  if (!email) return;
  /* try/catch is load-bearing, not defensive habit: syncFavsToContact() calls this from inside
     favs.toggle(), so an Omnisend that throws — broken, offline, or blocked by an extension —
     would take a shopper's favourite down with it. Found by the "a throwing Omnisend does not
     break favouriting" test when the sync call was added. */
  try {
    window.omnisend = window.omnisend || [];
    const payload = { email };
    /* Custom properties ride along with the identify so the contact arrives already segmentable.
       This is the ONLY route from a star to an email: Omnisend has no custom-event trigger, so
       favouriteAdded cannot start a workflow — but "entered segment" can, and segments filter on
       contact properties. */
    if (extra && typeof extra === 'object') Object.assign(payload, extra);
    window.omnisend.push(['identifyContact', payload]);
  } catch (_) { /* never allowed to break the interaction that triggered it */ }
}

/* ---------- favourites -> Omnisend ----------
   Favourites live in localStorage and nowhere else, which makes them invisible to every email
   automation. These events are the bridge.

   Fired for anonymous visitors too. If that browser is never identified the event is simply never
   used — no attempt is made here to rely on Omnisend stitching anonymous sessions to a contact
   after the fact, because that behaviour was not verified.

   Never throws and never blocks: a favourite must keep working with the marketing tool broken,
   offline, or blocked by an extension. */
function trackEvent(name, properties) {
  try {
    window.omnisend = window.omnisend || [];
    window.omnisend.push(['track', name, properties || {}]);
  } catch (_) { /* tracking is never allowed to break a favourite */ }
}

/* Sold-out reports are deduped through persistent storage, not memory: the availability pass re-runs
   on EVERY panel open, so without this a shopper who opens their favourites five times reports the
   same gone piece five times and the automation mails them five times. */
const FAVS_REPORTED_KEY = 'y2k-favs-gone';
function alreadyReportedGone(handle) {
  let seen = [];
  try { seen = JSON.parse(safeStorage.get(FAVS_REPORTED_KEY) || '[]'); } catch (_) { seen = []; }
  if (!Array.isArray(seen)) seen = [];
  if (seen.includes(handle)) return true;
  seen.push(handle);
  safeStorage.set(FAVS_REPORTED_KEY, JSON.stringify(seen.slice(-FAVS_MAX)));
  return false;
}

/* Push the current starred list onto the KNOWN contact, if there is one.

   Why this exists: Omnisend has no custom-event trigger — the trigger list is a fixed set (viewed
   product, added to cart, entered segment, ...). So favouriteAdded can never start a workflow.
   What CAN start one is "entered segment", and segments filter on contact PROPERTIES. That makes
   favouritesCount/favouriteHandles the only route from a star to an email, which in turn means they
   have to be kept current — not written once at signup.

   Only fires when the email is already known (logged in, via Y2K_CUSTOMER_EMAIL). Starring while
   logged out stays anonymous; there is nobody to attach it to and nothing to email. */
function syncFavsToContact() {
  const email = window.Y2K_CUSTOMER_EMAIL;
  if (!email) return;
  identifyContact(email, favsProperties());
}

/* The shopper's starred list, shaped for an Omnisend custom property. Handles only — titles and
   prices go stale, and the handle is what a campaign needs to build a product link. */
function favsProperties() {
  const list = favs.list();
  return {
    favouritesCount: list.length,
    favouriteHandles: list.slice(0, 20).map((s) => s.handle).join(','),
  };
}

function initEmailCapture() {
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form');
    if (!form) return;
    if (!form.matches('[data-y2k-identify]') && !form.closest('[data-y2k-identify]')) return;
    /* Deliberately input[type="email"] and not a looser selector: it is what makes
       checkValidity() mean something (a type="text" field validates anything) and it is what
       strips leading/trailing whitespace, since the platform sanitizes email values for us. */
    const field = form.querySelector('input[type="email"]');
    if (!field || !field.value || !field.checkValidity()) return;
    /* A form inside [data-y2k-identify-favs] sends the shopper's starred list with the identify.
       This is the single most valuable moment to do it: the favourites panel signup is the only
       place where an email address and a list of wanted one-of-ones are known at the same time. */
    const withFavs = form.closest('[data-y2k-identify-favs]');
    identifyContact(field.value, withFavs ? favsProperties() : null);
  });
}

window.y2k = { toast, sound, favs, refreshCartCount, openCartDrawer, identifyContact, trackEvent, syncFavsToContact, _cartVariants: new Set() };

/* initPdpPrice runs synchronously rather than off refreshCartCount(): the guard has to wait for
   /cart.js (it needs the cart's variant ids), but the price rewrite must not — chaining it there
   would leave "$69.00" on screen for the length of a network round-trip. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    wireChrome(); initSparkle(); initEmailCapture(); syncFavsToContact(); initPdpPrice(); initModalPrices(); initCartPrices(); refreshCartCount().then(initPdpGuard);
  });
} else {
  wireChrome(); initSparkle(); initEmailCapture(); syncFavsToContact(); initPdpPrice(); initModalPrices(); initCartPrices(); refreshCartCount().then(initPdpGuard);
}
